import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { SmartInsightsPropertyTagsUser } from 'lumen/models';

export type DropdownSmartInsightsDormFilterOption = Option & {
  id: string;
};

interface MarketSmartInsightsDropdownsDropdownDormFilterSignature {
  Args: {
    dorms: SmartInsightsPropertyTagsUser[];
  };
}

export default class MarketSmartInsightsDropdownsDropdownDormFilter extends Component<MarketSmartInsightsDropdownsDropdownDormFilterSignature> {
  @service declare intl: Services['intl'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownSmartInsightsDormFilterOption[] {
    return this.args.dorms
      .filter((dorm) => dorm.id !== null)
      .map((dorm) => ({
        label: dorm.name,
        value: dorm.id,
        id: dorm.id!,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDorms() {
    const { selectedDormIds } = this.portfolioSmartInsights;

    return this.options.filter((dormOption) =>
      selectedDormIds.includes(dormOption.id),
    );
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildLabelWithCount(count: number) {
    if (count === 0) {
      return this.intl.t('market.smart_insights.filter.dorms');
    }

    if (count === this.options.length) {
      return this.intl.t('market.smart_insights.filter.dorms.all_selected');
    }

    return this.intl.t('market.smart_insights.filter.dorms.count', {
      count,
    });
  }

  @action onChange(options: DropdownSmartInsightsDormFilterOption[]): void {
    const selectedDormIds = options.map((option) => option.id);

    this.portfolioSmartInsights.setSelectedDormIds(selectedDormIds);
  }

  <template>
    <PrismComboSelectInput
      @buildLabelWithCount={{this.buildLabelWithCount}}
      @canUnselectAll={{true}}
      @isSearchable={{true}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @selectAllLabel={{t "market.smart_insights.filter.dorms.all_selected"}}
      @value={{this.selectedDorms}}
    />
  </template>
}
